import { differenceInDays } from 'date-fns'
import { eachDayOfInterval, format } from 'date-fns'

/** Width for daily view (1 day). Fits one bar + padding. */
const WIDTH_DAILY_PX = 48
/** Width for weekly view (7 days). Fits 7 bars + gaps + padding. */
const WIDTH_WEEKLY_PX = 80

export type HourDistributionDataPoint = {
  date: string
  value: number
  plannedValue?: number
}

/**
 * Builds hour distribution dataPoints for the period: one entry per day from dateFrom to dateTo,
 * with worked minutes from workedTimeBlocks and planned minutes from estimatedTimes (missing days get 0).
 * plannedMinutes per day is used for two-tone coloring (underworked vs overworked/balanced).
 */
export function buildHourDistributionDataPoints(
  dateFrom: Date,
  dateTo: Date,
  workedTimeBlocks: Array<{ date: string; minutes: number }> | null | undefined,
  estimatedTimes: Array<{ date: string; minutes: number }> | null | undefined
): HourDistributionDataPoint[] {
  const workedByDate: Record<string, number> = {}
  if (workedTimeBlocks?.length) {
    for (const block of workedTimeBlocks) {
      const key = block.date
      workedByDate[key] = (workedByDate[key] ?? 0) + block.minutes
    }
  }
  const plannedByDate: Record<string, number> = {}
  if (estimatedTimes?.length) {
    for (const est of estimatedTimes) {
      const key = est.date
      plannedByDate[key] = (plannedByDate[key] ?? 0) + est.minutes
    }
  }
  const days = eachDayOfInterval({ start: dateFrom, end: dateTo })
  return days.map((day) => {
    const dateStr = format(day, 'yyyy-MM-dd')
    const value = workedByDate[dateStr] ?? 0
    const plannedValue = plannedByDate[dateStr]
    return {
      date: dateStr,
      value,
      ...(plannedValue != null ? { plannedValue } : {}),
    }
  })
}

/**
 * Returns column width for the distribution column based on number of days.
 * Daily (1) and weekly (7) get a smaller fixed width to reduce white space; monthly stays auto.
 */
export function getDistributionColumnWidth(dateFrom: Date, dateTo: Date): number | undefined {
  const numberOfDays = differenceInDays(dateTo, dateFrom) + 1
  if (numberOfDays <= 1) return WIDTH_DAILY_PX
  if (numberOfDays <= 7) return WIDTH_WEEKLY_PX
  return undefined
}
